import classNames from 'classnames';
import { Alert } from 'components/atoms/Alert';
import { Button } from 'components/atoms/Button';
import FreeTextInput from 'components/atoms/Form/FreeTextInput/FreeTextInput.component';
import useValidateSteuernummer from 'hooks/useValidateSteuernummer';
import { get } from 'lodash';
import { useEffect, useMemo, useState, type FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import type { TracingConfig } from 'utils/amplitude/amplitude.types';
import { germanStatesCodesMap, taxNumberPlaceholders } from 'utils/constants';
import { sanitizeSteuernummer } from 'utils/helpers';

interface SteuernummerFieldProps {
  name: string;
  value: string;
  error?: string;
  onChange: (...event: any[]) => void;
  onBlur?: () => void;
  autoComplete?: string;
  disabled?: boolean;
  className?: string;
  stateFieldName: string;
  taxOfficeStateFieldName?: string;
  isBankingOnboarding?: boolean;
  tracing?: TracingConfig<'onFocus'>;
  placeholder?: string;
}

const SteuernummerField: FC<SteuernummerFieldProps> = ({
  name,
  value,
  error: schemaError,
  ...props
}) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const [focused, setFocused] = useState(false);
  const stateValue = watch(props.stateFieldName);
  const taxOfficeStateValue = props.taxOfficeStateFieldName
    ? watch(props.taxOfficeStateFieldName)
    : undefined;
  const effectiveState = taxOfficeStateValue || stateValue;

  const { isValidatingSteuernummer } = useValidateSteuernummer({
    name,
    stateValue,
    taxOfficeStateValue,
    steuernummerValue: value,
  });
  const stateCode = get(germanStatesCodesMap, effectiveState?.toLowerCase());
  const placeholder = !effectiveState
    ? t('steuernummer.no_state.placeholder')
    : get(taxNumberPlaceholders, effectiveState?.toLowerCase());

  const isHessen = stateCode === 'DE-HE';
  useEffect(() => {
    if (isHessen && !value) props.onChange('0');
  }, [value, isHessen]);
  const isTaxId = sanitizeSteuernummer(value)?.length > 11;
  const isFedralScheme = sanitizeSteuernummer(value)?.length > 12;
  const isNotStandardScheme =
    sanitizeSteuernummer(value)?.length > 13 && /^[0-9/]+$/?.test(value);
  const error = useMemo(() => {
    if (!focused) {
      if (isNotStandardScheme)
        return {
          message: t('settings.steuernummer.validation.not_standard'),
          type: 'error',
        };
      if (isFedralScheme)
        return {
          message: t(
            'settings.steuernummer.validation.not_standard_remove_extra',
          ),
          type: 'error',
        };
      if (isTaxId)
        return {
          message: t('settings.steuernummer.validation.looks_like_tax_id'),
          type: 'error',
        };
      if (isHessen)
        return {
          message: t('settings.steuernummer.validation.hessen'),
          type: 'info',
        };
    }
  }, [value, focused, isHessen]);
  return (
    <>
      <FreeTextInput
        // {...props}
        placeholder={placeholder}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        disabled={!stateValue}
        max={13}
        value={value}
        onChange={(e) => props.onChange(e.target.value)}
        loading={isValidatingSteuernummer}
      />

      {!focused && !error && schemaError?.trim() && (
        <Alert className="mt-4" type="warning" title={t(schemaError)} />
      )}
      {error && (
        <Alert
          className="mt-2 py-1 px-2 !text-xs"
          type={error?.type as any}
          description={
            <ReactMarkdown
              components={{
                a: (linkProps) => (
                  <Button
                    onClick={() =>
                      Intercom(
                        'showNewMessage',
                        t(
                          props.isBankingOnboarding
                            ? 'settings.steuernummer.validation.prompt_message_banking'
                            : 'settings.steuernummer.validation.prompt_message',
                        ),
                      )
                    }
                    structure="text"
                    className={classNames({
                      '!text-yellow-700 hover:text-yellow-800':
                        error?.type === 'warning',
                      '!text-red-800 hover:text-red-900':
                        error?.type === 'error',
                    })}
                  >
                    {linkProps.children}
                  </Button>
                ),
              }}
            >
              {error?.message}
            </ReactMarkdown>
          }
        />
      )}
    </>
  );
};

export default SteuernummerField;
